import React from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSection } from '../contexts/ContentContext';

const Footer = () => {
  const data = useSection('footer', {
    tagline: 'Crafted in Lucknow · Curated for the World',
    email: 'info@silkandsentiment.com',
    phone: '+91 9151 060 995',
    phoneHref: 'tel:+919151060995',
    address: 'Lucknow, Uttar Pradesh\nBy Appointment Only',
    copyright: '© 2025 Silk & Sentiment. All rights reserved.',
  });

  return (
    <footer id="contact" style={styles.footer}>
      <div className="luxury-container">
        <div style={styles.footerContent}>
          <div style={styles.brandColumn}>
            <img
              src="/logo.png"
              alt="Silk & Sentiment"
              style={styles.logo}
              data-testid="footer-logo"
            />
            <p style={styles.tagline}>{data.tagline}</p>
          </div>

          <div style={styles.contactColumn}>
            <h4 style={styles.columnTitle}>Connect</h4>
            <div style={styles.contactList}>
              <a href={`mailto:${data.email}`} style={styles.contactItem}>
                <Mail size={16} color="var(--accent-gold)" />
                <span>{data.email}</span>
              </a>
              <a href={data.phoneHref || `tel:${(data.phone || '').replace(/\s/g, '')}`} style={styles.contactItem}>
                <Phone size={16} color="var(--accent-gold)" />
                <span>{data.phone}</span>
              </a>
              <div style={styles.contactItem}>
                <MapPin size={16} color="var(--accent-gold)" />
                <span>
                  {(data.address || '').split('\n').map((line, i, arr) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < arr.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </span>
              </div>
            </div>
          </div>

          <div style={styles.navColumn}>
            <h4 style={styles.columnTitle}>Explore</h4>
            <nav style={styles.footerNav}>
              <Link to="/about" style={styles.footerLink}>Our Story</Link>
              <Link to="/archive" style={styles.footerLink}>The Collection</Link>
              <Link to="/design-studio" style={styles.footerLink}>Craft Gallery</Link>
              <Link to="/contact" style={styles.footerLink}>Contact</Link>
            </nav>
          </div>

          <div style={styles.navColumn}>
            <h4 style={styles.columnTitle}>Atelier</h4>
            <nav style={styles.footerNav}>
              <Link to="/order-process" style={styles.footerLink} data-testid="footer-link-order-process">Order Process</Link>
              <Link to="/returns-exchange" style={styles.footerLink} data-testid="footer-link-returns">Returns &amp; Exchange</Link>
              <Link to="/track" style={styles.footerLink}>Track Your Commission</Link>
            </nav>
          </div>
        </div>

        <div style={styles.bottomSection}>
          <p style={styles.copyright}>{data.copyright}</p>
          <div style={styles.legalLinks}>
            <Link to="/returns-exchange" style={styles.legalLink}>Returns &amp; Exchange</Link>
            <span style={styles.separator}>·</span>
            <Link to="/order-process" style={styles.legalLink}>Order Process</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

const styles = {
  footer: {
    padding: '80px 0 40px',
    backgroundColor: 'var(--bg-secondary)',
    borderTop: '1px solid var(--border-light)',
  },
  footerContent: {
    display: 'grid',
    gridTemplateColumns: '1.5fr 1fr 1fr 1fr',
    gap: '60px',
    marginBottom: '60px',
  },
  brandColumn: {},
  logo: {
    height: '72px',
    width: 'auto',
    display: 'block',
    marginBottom: '12px',
  },
  tagline: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '13px',
    color: 'var(--text-secondary)',
    fontStyle: 'italic',
  },
  contactColumn: {},
  columnTitle: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '11px',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: 'var(--accent-gold)',
    marginBottom: '24px',
  },
  contactList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  contactItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    color: 'var(--text-secondary)',
    textDecoration: 'none',
    fontFamily: "'Inter', sans-serif",
    fontSize: '13px',
    lineHeight: '1.6',
    transition: 'color 0.3s ease',
  },
  navColumn: {},
  footerNav: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  footerLink: {
    color: 'var(--text-secondary)',
    textDecoration: 'none',
    fontFamily: "'Inter', sans-serif",
    fontSize: '13px',
    transition: 'color 0.3s ease',
  },
  bottomSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '24px',
    borderTop: '1px solid var(--border-light)',
  },
  copyright: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '12px',
    color: 'var(--text-light)',
  },
  legalLinks: {
    display: 'flex',
    gap: '8px',
  },
  legalLink: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '12px',
    color: 'var(--text-light)',
    textDecoration: 'none',
    transition: 'color 0.3s ease',
  },
  separator: {
    color: 'var(--text-light)',
  },
};

// Add hover styles
const hoverStyles = document.createElement('style');
hoverStyles.textContent = `
  footer a:hover {
    color: var(--text-primary) !important;
  }
  @media (max-width: 968px) {
    footer > div > div:first-child {
      grid-template-columns: 1fr 1fr !important;
      gap: 40px !important;
    }
  }
  @media (max-width: 768px) {
    footer > div > div:first-child {
      grid-template-columns: 1fr !important;
      gap: 40px !important;
    }
    footer > div > div:last-child {
      flex-direction: column !important;
      text-align: center !important;
      gap: 16px !important;
    }
  }
`;
document.head.appendChild(hoverStyles);

export default Footer;
